import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Entity,
  EntityTypeChange,
  insightsHeader,
  Octopus,
  SearchWorkflowTaskTemplate,
  WorkflowTaskTemplateFieldOption,
  WorkflowTaskTemplateProperty,
} from '@maximizer/core/shared/domain';
import { forkJoin, map, Observable, switchMap } from 'rxjs';
import { ContextService } from './context.service';

@Injectable()
export class WorkflowTaskTemplateService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  getQuery(
    params: SearchWorkflowTaskTemplate,
  ): Octopus.SearchQuery<Octopus.WorkflowTaskTemplate> {
    if (params.workflowKey) {
      return {
        WorkflowKey: {
          $EQ: params.workflowKey,
        },
      };
    } else if (params.workflowKeys) {
      return {
        WorkflowKey: {
          $IN: params.workflowKeys,
        },
      };
    } else if (params.key) {
      return {
        Key: {
          $EQ: params.key,
        },
      };
    }
    return {};
  }

  read(
    param: SearchWorkflowTaskTemplate,
  ): Observable<Entity | Entity[] | null> {
    const query = this.getQuery(param);
    const request: Octopus.WorkflowTaskTemplateReadRequest = {
      WorkflowTaskTemplate: {
        Scope: {
          Fields: {
            Key: 1,
            WorkflowKey: 1,
            Sequence: 1,
            Subject: 1,
            DateOffsetUnit: 1,
            DateOffsetValue: 1,
            Time: 1,
            AlarmOffsetUnit: 1,
            AlarmOffsetValue: 1,
            Priority: 1,
            IconType: 1,
            Category: 1,
            AssignedToExpression: 1,
          },
        },
        Criteria: {
          SearchQuery: query,
        },
      },
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.EntityResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            if (result.WorkflowTaskTemplate?.Data.length === 1 && param.key) {
              return result.WorkflowTaskTemplate?.Data[0];
            }
            return result.WorkflowTaskTemplate?.Data as Entity[];
          }
          return null;
        }),
      );
  }

  readForGrid(
    param: SearchWorkflowTaskTemplate,
  ): Observable<WorkflowTaskTemplateProperty[]> {
    const query = this.getQuery(param);
    const request: Octopus.WorkflowTaskTemplateReadRequest = {
      WorkflowTaskTemplate: {
        Scope: {
          Fields: {
            Key: 1,
            WorkflowKey: 1,
            Sequence: 1,
            Subject: 1,
            DateOffsetUnit: 1,
            DateOffsetValue: 1,
            Time: 1,
            AlarmOffsetUnit: 1,
            AlarmOffsetValue: 1,
            Priority: { Value: 1, DisplayValue: 1 },
            IconType: { Value: 1, DisplayValue: 1 },
            Category: { Value: 1, DisplayValue: 1 },
            AssignedToExpression: { Value: 1, DisplayValue: 1 },
          },
        },
        Criteria: {
          SearchQuery: query,
        },
      },
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.WorkflowTaskTemplateReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.WorkflowTaskTemplatePropertyMapper();
            return mapper.from(result.WorkflowTaskTemplate.Data);
          }
          return [];
        }),
      );
  }

  update(values: Entity): Observable<string | null> {
    const request: Octopus.WorkflowTaskTemplateWriteRequest = {
      WorkflowTaskTemplate: {
        Data: values,
      },
      Globalization: Octopus.DefaultGlobalization,
    };
    return this.http
      .post<Octopus.WorkflowTaskTemplateWriteResponse>(
        `${this.context.api}${values[Octopus.EntityFields.Key] ? Octopus.Action.UPDATE : Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.WorkflowTaskTemplate.Data.Key;
          }
          return null;
        }),
      );
  }

  copy(
    workflowKey: string,
    values: Entity[],
    entityTypeChange?: EntityTypeChange,
  ): Observable<boolean> {
    if (!entityTypeChange) {
      return this.createTemplates(workflowKey, values);
    }
    return this.getAssignedToExpressionOptions(entityTypeChange).pipe(
      switchMap(({ oldOptions, newOptions }) => {
        values.forEach((value) => {
          const expression =
            value[Octopus.WorkflowTaskTemplateFields.AssignedToExpression];
          if (typeof expression === 'string') {
            value[Octopus.WorkflowTaskTemplateFields.AssignedToExpression] =
              Octopus.normalizeAssignedToExpression(
                expression,
                oldOptions,
                newOptions,
              );
          }
        });
        return this.createTemplates(workflowKey, values);
      }),
    );
  }

  getAssignedToExpressionOptions(
    entityTypeChange: EntityTypeChange,
  ): Observable<{ oldOptions: string[]; newOptions: string[] }> {
    // Disabled users are valid assignees whatever the display preference, so they
    // must be in both lists or a template assigned to one would be remapped.
    return forkJoin({
      oldOptions: this.getFieldOption(true, entityTypeChange.from),
      newOptions: this.getFieldOption(true, entityTypeChange.to),
    }).pipe(
      map(({ oldOptions, newOptions }) => ({
        oldOptions: oldOptions.assignedToExpression.map((option) => option.id),
        newOptions: newOptions.assignedToExpression.map((option) => option.id),
      })),
    );
  }

  private createTemplates(
    workflowKey: string,
    values: Entity[],
  ): Observable<boolean> {
    const request: { [name: string]: unknown } = {};
    values.forEach((value, index) => {
      value[Octopus.EntityFields.Key] = null;
      value[Octopus.WorkflowTaskTemplateFields.WorkflowKey] = workflowKey;
      request['WorkflowTaskTemplate.' + index] = { Data: value };
    });
    return this.http
      .post<Octopus.WorkflowTaskTemplateWriteResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((result) => {
          return result.Code === Octopus.ResponseStatusCode.Successful;
        }),
      );
  }

  updateBatch(
    items: { key: string; assignedToExpression: string }[],
  ): Observable<boolean> {
    const request: { [name: string]: unknown } = {};
    items.forEach((value, index) => {
      request['WorkflowTaskTemplate.' + index] = {
        Data: {
          Key: value.key,
          AssignedToExpression: value.assignedToExpression,
        },
      };
    });
    return this.http
      .post<Octopus.WorkflowTaskTemplateWriteResponse>(
        `${this.context.api}${Octopus.Action.UPDATE}`,
        request,
      )
      .pipe(
        map((result) => {
          return result.Code === Octopus.ResponseStatusCode.Successful;
        }),
      );
  }

  reorder(items: WorkflowTaskTemplateProperty[]): Observable<boolean> {
    const request: { [name: string]: unknown } = {
      Configuration: { Transaction: true },
    };
    items.forEach((value, index) => {
      request['WorkflowTaskTemplate.' + index] = {
        Data: { Key: value.key, Sequence: value.sequence },
      };
    });
    return this.http
      .post<Octopus.WorkflowTaskTemplateWriteResponse>(
        `${this.context.api}${Octopus.Action.UPDATE}`,
        request,
      )
      .pipe(
        map((result) => {
          return result.Code === Octopus.ResponseStatusCode.Successful;
        }),
      );
  }

  delete(key: string): Observable<boolean> {
    const request = {
      WorkflowTaskTemplate: {
        Data: {
          Key: key,
        },
      },
    };
    return this.http
      .post<Octopus.WorkflowTaskTemplateWriteResponse>(
        `${this.context.api}${Octopus.Action.DELETE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return true;
          }
          return false;
        }),
      );
  }

  getFieldOption(
    showDisabledUsers?: boolean,
    entityType?: Octopus.EntityType,
  ): Observable<WorkflowTaskTemplateFieldOption> {
    const request: Octopus.WorkflowTaskTemplateFieldOptionsRequest = {
      WorkflowTaskTemplate: {
        ...(entityType != null ? { Data: { EntityType: entityType } } : {}),
        FieldOptions: {
          Priority: [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
          Category: [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
          DateOffsetUnit: [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
          AlarmOffsetUnit: [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
          AssignedToExpression: [
            {
              Key: 1,
              DisplayValue: 1,
            },
          ],
        },
        Options: {
          IncludeDisabled: showDisabledUsers,
        },
      },
    };

    return this.http
      .post<Octopus.WorkflowTaskTemplateFieldOptionsResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
        { headers: this.getRequestHeaders() },
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.WorkflowTaskTemplateFieldOptionsMapper();
            return mapper.from(result);
          }
          return {
            priority: [],
            category: [],
            alarmOffsetUnit: [],
            dateOffsetUnit: [],
            assignedTo: [],
            assignedToExpression: [],
            result: [],
          };
        }),
      );
  }

  private getRequestHeaders(): { [header: string]: string } {
    return {
      [insightsHeader('entity')]: 'WorkflowTaskTemplate',
    };
  }
}
